import A11yDialog from 'a11y-dialog';
import { onBxFormSubmit } from '../../lib/onBxFormSubmit';
import { ym } from '../../lib/ym';

const pdrEstimateMaxFiles = 5;
const pdrEstimateMaxFileSize = 10 * 1024 * 1024;

type PdrEstimatePhoto = {
  file: File;
  url: string;
};

function createFileList(file: File): FileList | null {
  try {
    const transfer = new DataTransfer();
    transfer.items.add(file);
    return transfer.files;
  } catch {
    return null;
  }
}

function revokePhoto(photo: PdrEstimatePhoto) {
  URL.revokeObjectURL(photo.url);
}

function initPdrEstimateForm(form: HTMLFormElement) {
  if (form.dataset.pdrEstimateInitialized === 'true') {
    return;
  }

  const upload = form.querySelector<HTMLElement>('[data-pdr-upload]');
  const trigger = form.querySelector<HTMLElement>('[data-pdr-upload-trigger]');
  const empty = form.querySelector<HTMLElement>('[data-pdr-upload-empty]');
  const previews = form.querySelector<HTMLElement>('[data-pdr-upload-previews]');
  const status = form.querySelector<HTMLElement>('[data-pdr-upload-status]');
  const inputs = Array.prototype.slice.call(
    form.querySelectorAll<HTMLInputElement>('[data-pdr-photo-input]')
  );
  const photos: PdrEstimatePhoto[] = [];

  if (!upload || !trigger || !empty || !previews || inputs.length === 0) {
    return;
  }

  form.dataset.pdrEstimateInitialized = 'true';

  const setStatus = (message: string) => {
    if (!status) {
      return;
    }

    status.textContent = message;
    status.hidden = message === '';
  };

  const syncInputs = () => {
    inputs.forEach((input, index) => {
      const photo = photos[index];
      const files = photo ? createFileList(photo.file) : null;

      if (files) {
        input.files = files;
      } else {
        input.value = '';
      }
    });
  };

  const render = () => {
    const hasPhotos = photos.length > 0;

    upload.classList.toggle('is-filled', hasPhotos);
    empty.hidden = hasPhotos;
    previews.hidden = !hasPhotos;
    previews.innerHTML = '';

    photos.forEach((photo, index) => {
      const item = document.createElement('div');
      item.className = 'pdr-estimate-form__preview';

      const image = document.createElement('img');
      image.className = 'pdr-estimate-form__preview-image';
      image.src = photo.url;
      image.alt = '';

      const button = document.createElement('button');
      button.className = 'pdr-estimate-form__preview-delete';
      button.type = 'button';
      button.setAttribute('aria-label', 'Удалить фото');

      const deleteIcon = document.createElement('img');
      deleteIcon.className = 'pdr-estimate-form__preview-delete-icon';
      deleteIcon.src = '/pdr/assets/pdr-upload-x-close.svg';
      deleteIcon.alt = '';
      deleteIcon.setAttribute('aria-hidden', 'true');

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const removed = photos.splice(index, 1)[0];
        if (removed) {
          revokePhoto(removed);
        }

        setStatus('');
        syncInputs();
        render();
      });

      button.appendChild(deleteIcon);
      item.appendChild(image);
      item.appendChild(button);
      previews.appendChild(item);
    });
  };

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) {
      return;
    }

    const incoming = Array.from(fileList);
    let rejected = false;
    let overLimit = false;

    incoming.forEach((file) => {
      if (photos.length >= pdrEstimateMaxFiles) {
        overLimit = true;
        return;
      }

      if (!file.type.match(/^image\//)) {
        rejected = true;
        return;
      }

      if (file.size > pdrEstimateMaxFileSize) {
        rejected = true;
        return;
      }

      photos.push({
        file,
        url: URL.createObjectURL(file),
      });
    });

    if (overLimit || incoming.length > pdrEstimateMaxFiles) {
      setStatus('Можно загрузить не больше 5 фото.');
    } else if (rejected) {
      setStatus('Загрузите изображения до 10 Мб.');
    } else {
      setStatus('');
    }

    syncInputs();
    render();
  };

  const openChooser = () => {
    const chooser = document.createElement('input');
    chooser.type = 'file';
    chooser.accept = 'image/*';
    chooser.multiple = true;
    chooser.addEventListener('change', () => {
      addFiles(chooser.files);
      chooser.remove();
    });
    chooser.click();
  };

  trigger.addEventListener('click', openChooser);
  trigger.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    openChooser();
  });

  form.addEventListener('reset', () => {
    photos.splice(0).forEach(revokePhoto);
    setStatus('');
    syncInputs();
    render();
  });

  window.addEventListener('beforeunload', () => {
    photos.forEach(revokePhoto);
  });

  render();
}

function initPdrEstimateForms(root: ParentNode = document) {
  root.querySelectorAll<HTMLFormElement>('.js-pdr-estimate-form').forEach((form) => {
    initPdrEstimateForm(form);
  });
}

function initPdrEstimateDialogs() {
  const successElement = document.getElementById('pdr-estimate-success-dialog');
  const failureElement = document.getElementById('pdr-estimate-failure-dialog');

  if (!successElement || !failureElement) {
    return;
  }

  const successDialog = new A11yDialog(successElement);
  const failureDialog = new A11yDialog(failureElement);
  const formContainer = document.querySelector<HTMLElement>('.pdr-estimate-form-container');
  let lastFailureText = '';
  let successShown = false;

  const createRestorableFormHtml = () => {
    if (!formContainer) {
      return '';
    }

    const clone = formContainer.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return '';

    clone.querySelectorAll<HTMLFormElement>('.js-pdr-estimate-form').forEach((form) => {
      delete form.dataset.pdrEstimateInitialized;
      form.reset();
    });

    clone.querySelectorAll<HTMLElement>('[data-pdr-upload]').forEach((upload) => {
      upload.classList.remove('is-filled');
    });

    clone.querySelectorAll<HTMLElement>('[data-pdr-upload-empty]').forEach((empty) => {
      empty.hidden = false;
    });

    clone.querySelectorAll<HTMLElement>('[data-pdr-upload-previews]').forEach((previews) => {
      previews.hidden = true;
      previews.innerHTML = '';
    });

    clone.querySelectorAll<HTMLElement>('[data-pdr-upload-status]').forEach((status) => {
      status.hidden = true;
      status.textContent = '';
    });

    return clone.innerHTML;
  };

  const initialFormHtml = createRestorableFormHtml();

  const restoreForm = () => {
    if (!formContainer || initialFormHtml === '') {
      return;
    }

    formContainer.innerHTML = initialFormHtml;
    initPdrEstimateForms(formContainer);
  };

  const hasSuccessText = () =>
    /Ваша заявка\s+\d+\s+принята/i.test(formContainer?.textContent || '');

  const isSuccessState = () =>
    Boolean(formContainer?.querySelector('[data-pdr-estimate-success]')) ||
    Boolean(formContainer?.querySelector('.notetext')) ||
    (!formContainer?.querySelector('form') &&
      !formContainer?.querySelector('.errortext') &&
      hasSuccessText());

  const showSuccess = () => {
    if (successShown) {
      return;
    }

    successShown = true;
    restoreForm();
    successDialog.show();
    ym.reachGoal('pdr_form_photo');
  };

  formContainer?.addEventListener(
    'submit',
    () => {
      lastFailureText = '';
      successShown = false;
    },
    true
  );

  onBxFormSubmit('.pdr-estimate-form-container', ({ isSuccess }) => {
    initPdrEstimateForms();

    if (isSuccess) {
      showSuccess();
      return;
    }

    const failureText = formContainer?.querySelector('.errortext')?.textContent?.trim() || '';
    if (failureText !== '' && failureText === lastFailureText) {
      return;
    }

    lastFailureText = failureText;
    failureDialog.show();
  });

  if (!formContainer) {
    return;
  }

  const observer = new MutationObserver(() => {
    if (isSuccessState()) {
      showSuccess();
    }
  });

  observer.observe(formContainer, { childList: true, subtree: true });
}

export function estimate() {
  initPdrEstimateForms();
  initPdrEstimateDialogs();

  const observer = new MutationObserver((mutationList) => {
    mutationList.forEach((mutation) => {
      if (mutation.type === 'childList') {
        initPdrEstimateForms();
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}
